import {
  AuthState,
  OktaAuthCoreInterface,
  OktaAuthOAuthInterface,
} from '@okta/okta-auth-js';
import logEvent, { LogEvent, LogEventStatus } from 'utils/logEvent';
import windowPostMessage from 'utils/windowPostMessage';
import allowReturnToUrl from 'pages/Login/allowReturnToUrl';
import getSupportRedirectUrl from 'utils/getSupportRedirectUrl';
import type { WindowConfig } from '../global';

const baseConfig = {
  version: process.env.REACT_APP_VERSION,
};
const matchesReactAppTarget = (env: string) =>
  env.toLowerCase() === process.env.REACT_APP_TARGET?.toLowerCase();

export const matchesEnvironment = (env: string) =>
  process.env.REACT_APP_TARGET
    ? matchesReactAppTarget(env)
    : window.location.hostname.startsWith(env);

const baseOidcConfig = {
  transformAuthState: async (
    oktaAuth: OktaAuthCoreInterface,
    authState: AuthState,
  ): Promise<AuthState> => {
    if (!authState?.isAuthenticated) {
      return authState;
    }
    const sessionExists = await oktaAuth?.session?.exists();
    if (!sessionExists) {
      oktaAuth?.tokenManager?.clear();
    }

    return {
      ...authState,
      isAuthenticated: authState.isAuthenticated && sessionExists,
    };
  },
  restoreOriginalUri: async (
    oktaAuth: OktaAuthOAuthInterface,
    originalUri: string,
  ) => {
    let url: URL;
    try {
      url = new URL(originalUri as string);
    } catch {
      url = new URL(window.location.origin);
    }
    const { authType } = JSON.parse(
      sessionStorage.getItem('loginData') || '{}',
    );

    const valid = allowReturnToUrl(url);

    const isAuthenticated = await oktaAuth.isAuthenticated();

    if (!isAuthenticated) {
      return;
    }

    if (sessionStorage.getItem('idpr') === 'true') {
      const userinfo = await oktaAuth.token?.getUserInfo();
      logEvent(LogEvent.IdP, LogEventStatus.Successful, {
        username: userinfo?.preferred_username,
      });
      sessionStorage.removeItem('idpr');
    }
    if (authType === 'popup') {
      windowPostMessage({ isAuthenticated, originalUri });
      return;
    }

    const supportRedirectUrl = getSupportRedirectUrl(oktaAuth.getAccessToken());

    if (supportRedirectUrl) {
      window.location.replace(supportRedirectUrl);
      return;
    }

    if (
      url.href !== window.location.origin &&
      url.href !== window.location.href &&
      valid
    ) {
      window.location.replace(url.href);
    } else {
      window.location.replace(configValue.dashboard);
    }
  },
};

const configValue: WindowConfig & typeof baseConfig = {
  ...baseConfig,
  ...window._config,
};
configValue.oidc = { ...baseOidcConfig, ...window._config?.oidc };
export default configValue;
